import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * What to show when a screen breaks.
 *
 * Without this, one bad value anywhere in a page empties the browser window —
 * no message, no menu, no way back, and nothing for anyone to report beyond
 * "it went white". A shop cannot work with that, and neither can whoever has
 * to fix it.
 *
 * So a failure is caught here and stated: what broke, on which screen, with a
 * way back to work and the exact error to pass on. The rest of the app keeps
 * running.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Kept in the console for whoever is looking, with the component trail.
    console.error('Screen failed:', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="grid min-h-[60vh] place-items-center p-6">
        <div className="w-full max-w-lg rounded-xl border border-danger/30 bg-surface p-6 shadow-card">
          <h1 className="text-lg font-semibold">This screen could not be shown</h1>
          <p className="mt-1 text-sm text-ink-soft">
            Nothing has been lost — whatever was already saved is safe. The rest of the app is still working.
          </p>

          <pre className="mt-4 max-h-40 overflow-auto rounded-md bg-silver-50 p-3 text-2xs text-ink-soft">
            {error.message}
          </pre>

          <div className="mt-4 flex flex-wrap gap-2">
            <button className="btn-primary" onClick={() => this.setState({ error: null })}>
              Try again
            </button>
            <button
              className="btn-ghost"
              onClick={() => {
                this.setState({ error: null });
                window.location.assign('/');
              }}
            >
              Back to Today
            </button>
            <button className="btn-quiet" onClick={() => window.location.reload()}>
              Reload
            </button>
          </div>

          <p className="mt-3 text-2xs text-ink-faint">
            If it keeps happening, send this message and the address in the bar — that is enough to find it.
          </p>
        </div>
      </div>
    );
  }
}
